import { withdrawalWarning } from "../../services/hiveActions";

/**
 * Honey that must not go in a jar yet (#404).
 *
 * Asked from a device while testing #398's withdrawal-period field:
 *
 *     "should we add a warning or alert if you're within a withdrawal
 *      period for the 'Pull Frames' and 'Harvest' forms to inform the
 *      user they shouldn't pull honey until 'X' date due to treatment
 *      withdrawal?"
 *
 * ── ONE COMPONENT BECAUSE THERE IS MORE THAN ONE DOOR ──
 *
 * Honey leaves a hive here by two routes -- the log-action dialog (Pull
 * Frames) and the harvest form -- and by three on mobile. Writing the
 * warning at the first one and calling the task done is the one-path bug
 * this codebase keeps finding: a feature that works, but only down one
 * route into it.
 *
 * ── A BANNER, NOT A CONFIRM, AND THAT IS A DELIBERATE DEPARTURE ──
 *
 * #404 left this open: "the banner is calmer and easier to ignore; the
 * confirm is the pattern #375 just established for duplicates.
 * Consistency argues for confirm, noise argues for banner."
 *
 * Banner, because the two warnings are not the same shape. #375's
 * duplicate check fires on a COINCIDENCE -- the same action twice in one
 * day -- which is rare, genuinely ambiguous, and answerable only by
 * asking. A withdrawal period is a FACT, known before the form opens,
 * and it stays true for every harvest from that hive for weeks. A confirm
 * would fire predictably, repeatedly, and on honey that is often fine
 * anyway -- comb honey cut before the treatment, frames pulled for brood
 * -- and a dialog somebody dismisses twenty times is one they stop
 * reading, including the twenty-first time when it mattered.
 *
 * ── IT INFORMS, IT DOES NOT BLOCK ──
 *
 * For the same reasons, and because people route around blocks.
 *
 * ── IT SAYS THE DATE ──
 *
 * "You are inside a withdrawal period" sends somebody to look it up. The
 * sentence comes from services/hiveActions, so the phone and the desk
 * cannot explain one food-safety fact two different ways.
 */
export function WithdrawalNotice({ withdrawal, hiveId, actionType }) {
  const note = withdrawalWarning(withdrawal, hiveId, actionType);

  // Nothing to say is the usual case: most hives are not in withdrawal
  // most of the time, and an empty box on every harvest form would be the
  // noise that gets the real warning ignored.
  if (!note) return null;

  return (
    <p
      role="alert"
      style={{
        margin: 0,
        padding: "var(--space-2) var(--space-3)",
        borderLeft: "3px solid var(--color-danger)",
        background: "var(--color-danger-subtle, transparent)",
        color: "var(--color-text)",
      }}
    >
      <strong>Still inside a withdrawal period.</strong> {note}
    </p>
  );
}
